import * as React from 'react';
import { Button, Checkbox, MultiSelect } from '@cian/ui-kit';

import { validateEmail } from '../../utils/validateEmail';
import { IRegionsItem } from '../../types/regions';
import { IUser } from '../../types/user';
import { ISubscribe } from '../../types/subscribe';
import { getSubCategory, categories } from '../../../app/helpers/category_helper';
import { Tooltip } from '../tooltip/tooltip';

import s from './subscribe_list.css';

interface IProps {
  listType: string;
  subscribeStatus: ISubscribe;
  currentUser: IUser;
  regions: IRegionsItem[];
  listSubscribe?(email: string, categories?: string[], regions?: number[]): void;
}

export const SubscribeList: React.FC<IProps> = ({ listType, subscribeStatus, currentUser, regions, listSubscribe }) => {
  const [isEmailSend, setIsEmailSend] = React.useState(false);
  const [subscribeDone, setSubscribeDone] = React.useState(false);
  const [emailText, setEmailText] = React.useState(currentUser ? currentUser.email : '');
  const [showContent, setShowContent] = React.useState(false);
  const [emailValid, setEmailValid] = React.useState(true);
  const [selectedRegion, setSelectedRegion] = React.useState<number[]>([0]);
  const [selectedCategories, setSelectedCategories] = React.useState<string[]>(
    Object.keys(categories).filter(key => !!key),
  );

  const RESPONSE_ERROR = 400;

  const prevStatusRef = React.useRef(subscribeStatus);

  React.useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = subscribeStatus;

    if (subscribeStatus === prevStatus) {
      return;
    }

    if (subscribeStatus.status === 'ok') {
      setIsEmailSend(true);
      setSubscribeDone(true);
      setEmailValid(true);
    } else if (subscribeStatus.statusCode === RESPONSE_ERROR) {
      setIsEmailSend(true);
      setSubscribeDone(false);
      setEmailValid(true);
    }
  }, [subscribeStatus]);

  const onSubmitSubscribe = () => {
    if (!validateEmail(emailText)) {
      setEmailValid(false);
      return;
    }

    setEmailValid(true);

    let finalRegions = selectedRegion;
    if (finalRegions.indexOf(0) !== -1) {
      finalRegions = [];
    }

    if (listSubscribe) {
      if (!listType.toLowerCase().startsWith('question')) {
        listSubscribe(emailText);
      } else {
        listSubscribe(emailText, selectedCategories, finalRegions);
      }
    }
  };

  const category = getSubCategory(listType.toLowerCase()).category;

  const renderRegionsCategories = () => {
    return (
      <div>
        <div className={s['subscribe-list__wrap-regions']}>
          <span>Регион:</span>
          <div className={s['subscribe-list__wrap-regions_dropdown-wrap']}>
            <MultiSelect
              placeholder="Выберите регион"
              value={selectedRegion}
              onChange={(value: number[]) => {
                if (value[value.length - 1] === 0) {
                  setSelectedRegion([0]);
                } else {
                  setSelectedRegion(value.filter((val: number) => val !== 0));
                }
              }}
              options={regions.map((region: IRegionsItem) => ({
                value: region.id,
                label: region.displayName,
              }))}
            />
          </div>
        </div>
        <div className={s['subscribe-list__wrap-categories-wrap']}>
          {Object.keys(categories)
            .filter((key: string) => !!key)
            .map((key: string) => (
              <div
                className={s['subscribe-list__wrap-categories-wrap_checkbox-container-wrap']}
                key={`check-category-${key}`}
              >
                <Checkbox
                  label={categories[key].name}
                  checked={selectedCategories.includes(key)}
                  onChange={(_, value: boolean) => {
                    setSelectedCategories(prev => {
                      if (value && !prev.includes(key)) {
                        return [...prev, key];
                      } else if (!value && prev.includes(key)) {
                        return prev.filter(c => c !== key);
                      }
                      return prev;
                    });
                  }}
                />
              </div>
            ))}
        </div>
      </div>
    );
  };

  const subscribeSuccess = () => {
    const isQuestion = listType === 'Question';
    return (
      <div
        className={`
          ${s['subscribe-list__success']}
          ${isQuestion ? s['subscribe-list__success-question'] : ''}
        `}
      >
        <span className={s['subscribe-list__success_header']}>Подписка оформлена!</span>
        <span className={s['subscribe-list__success_text']}>
          Мы будем присылать письма на{' '}
          <span className={s['subscribe-list__success_email']}>{emailText}</span>
        </span>
      </div>
    );
  };

  const subscribeFailure = () => {
    const isQuestion = listType === 'Question';
    return (
      <div
        className={`
          ${s['subscribe-list__error']}
          ${isQuestion ? s['subscribe-list__error-question'] : ''}
        `}
      >
        <span className={s['subscribe-list__error_header']}>Что-то пошло не так, попробуйте подписаться еще раз</span>
        <Button
          theme="fill_primary"
          size="XS"
          onClick={() => {
            setIsEmailSend(false);
            setSubscribeDone(false);
            setEmailText(currentUser ? currentUser.email : '');
            setShowContent(true);
            setEmailValid(true);
          }}
        >
          Повторить
        </Button>
      </div>
    );
  };

  return (
    <div className={s['subscribe-list']}>
      <div
        className={`
          ${s['subscribe-list__header']}
          ${showContent ? s['subscribe-list__header-open-content-mobile'] : ''}
        `}
        onClick={() => setShowContent(prev => !prev)}
      >
        <span className={s['subscribe-list__header_mail-icon']} />
        <span className={s['subscribe-list__header_text-dekstop']}>подпишитесь на новые {category}</span>
        <span className={s['subscribe-list__header_text-mobile']}>подписаться на {category}</span>
        <span
          className={`
          ${s['subscribe-list__header_arrow-icon']}
          ${showContent ? s['subscribe-list__header_arrow-icon-open-content'] : ''}
        `}
        />
      </div>
      {showContent && (
        <form
          className={s['subscribe-list__content']}
          onSubmit={e => {
            e.preventDefault();
            if (!isEmailSend) {
              onSubmitSubscribe();
            }
          }}
        >
          {listType.toLowerCase().startsWith('question') && renderRegionsCategories()}
          {isEmailSend ? (
            subscribeDone ? subscribeSuccess() : subscribeFailure()
          ) : (
            <div>
              <Tooltip
                containerStyle={s['tooltip-email']}
                content={emailText ? 'Некорректный адрес email' : 'Укажите email'}
                show={!emailValid}
              >
                <input
                  type="text"
                  className={s['subscribe-list__content_email']}
                  disabled={!!currentUser && !!currentUser.email}
                  value={emailText}
                  onChange={(e: React.FormEvent<HTMLInputElement>) => {
                    setEmailText((e.currentTarget as HTMLInputElement).value);
                    setEmailValid(true);
                  }}
                  placeholder="Ваш email"
                />
              </Tooltip>
              <button className={s['subscribe-list__content_button']}>Подписаться</button>
              <div className={s['license']}>
                <span>При подписке вы принимаете условия</span>
                <br />
                <a href="//www.cian.ru/help/about/rules/" target="_blank" className={s['link']} rel="noreferrer">
                  Пользовательского соглашения
                </a>
                <span> и </span>
                <a href="//www.cian.ru/help/about/36526/" target="_blank" className={s['link']} rel="noreferrer">
                  Политики конфиденциальности
                </a>
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );
};
